import { useEffect } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import type { ProfileField, ProfileFieldValue } from "../../types";
import { DynamicFieldControl } from "./DynamicFieldControl";
import {
  initialDynamicFieldValue,
  isFieldMasked,
  validateDynamicField,
} from "./dynamicFields";

type DynamicFieldProps<TFieldValues extends FieldValues> = {
  control?: Control<TFieldValues>;
  field: ProfileField;
  idPrefix?: string;
  name: FieldPath<TFieldValues>;
  requiredIndicator?: boolean;
  rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
};

export function DynamicField<TFieldValues extends FieldValues>({
  control,
  field,
  idPrefix,
  name,
  requiredIndicator = false,
  rules,
}: DynamicFieldProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const resolvedControl = control ?? formContext.control;
  const watchedValues = useWatch({ control: resolvedControl }) as TFieldValues;
  const currentFieldValue = useWatch({
    control: resolvedControl,
    name,
  }) as ProfileFieldValue | undefined;
  const profileFieldValues =
    (watchedValues as { profileFields?: Record<string, ProfileFieldValue> })
      ?.profileFields ?? {};
  const masked = isFieldMasked(field, profileFieldValues);
  useEffect(() => {
    if (!masked) {
      return;
    }

    const emptyValue = initialDynamicFieldValue(field);
    const normalizedCurrentValue = normalizeComparableValue(
      currentFieldValue ?? null,
    );
    const normalizedEmptyValue = normalizeComparableValue(emptyValue);

    if (normalizedCurrentValue !== normalizedEmptyValue) {
      formContext.setValue(
        name,
        emptyValue as TFieldValues[FieldPath<TFieldValues>],
        {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        },
      );
    }
  }, [currentFieldValue, field, formContext, masked, name]);

  return (
    <Controller
      control={resolvedControl}
      name={name}
      rules={{
        validate: (value) =>
          validateDynamicField(
            field,
            value as ProfileFieldValue | undefined,
            profileFieldValues,
          ),
        ...rules,
      }}
      render={({ field: controlledField, fieldState: { error } }) => {
        if (masked) {
          return <></>;
        }

        return (
          <DynamicFieldControl
            error={error}
            field={field}
            idPrefix={idPrefix}
            onChange={controlledField.onChange}
            requiredIndicator={requiredIndicator}
            value={
              (controlledField.value as ProfileFieldValue | undefined) ??
              initialDynamicFieldValue(field)
            }
          />
        );
      }}
    />
  );
}

function normalizeComparableValue(value: ProfileFieldValue): string {
  return Array.isArray(value) ? JSON.stringify(value) : String(value ?? "");
}
